import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Chip,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Box,
  Alert,
  CircularProgress,
  TableSortLabel,
  Toolbar,
} from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import {
  useGetEventQuery,
  useAddAttendeesMutation,
  useRemoveAttendeesMutation,
  usePreviewParticipantsByCriteriaMutation,
  useAddParticipantsByCriteriaMutation,
} from "../../features/events/eventsApiSlice";
import { useGetMembersQuery } from "../../features/members/membersApiSlice";
import { useGetSitesQuery } from "../../features/sites/sitesApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import { gradArray } from "../form/fields/inputFields";

const toId = (value) => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  return String(value?._id || value?.id || "");
};
const toMemberName = (member) => {
  const lastName = member?.lastName || "";
  const firstName = member?.firstName || "";
  const fullName = [lastName, firstName].filter(Boolean).join(", ");
  return (
    fullName || member?.memberNumber || toId(member) || "Unbekanntes Mitglied"
  );
};
const gradLabel = (degree) =>
  gradArray.find((g) => g.value === Number(degree))?.label ||
  String(degree ?? "");

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function EventParticipantsDialog({
  open,
  onClose,
  eventId,
  eventTitle,
}) {
  const dispatch = useDispatch();
  const [managementTab, setManagementTab] = useState(0);
  const [consentTab, setConsentTab] = useState(0);

  const {
    data: event,
    isFetching: isFetchingEvent,
    refetch,
  } = useGetEventQuery(eventId, { skip: !eventId });
  const [addAttendees, { isLoading: isAdding }] = useAddAttendeesMutation();
  const [removeAttendees, { isLoading: isRemoving }] =
    useRemoveAttendeesMutation();
  const [previewByCriteria, { isLoading: isPreviewing }] =
    usePreviewParticipantsByCriteriaMutation();
  const [addByCriteria, { isLoading: isAddingByCriteria }] =
    useAddParticipantsByCriteriaMutation();

  const { data: members = [], isLoading: isLoadingMembers } =
    useGetMembersQuery();
  const { data: sites = [] } = useGetSitesQuery();

  const registeredParticipants = useMemo(() => {
    const pricingOptions = Array.isArray(event?.pricingOptions)
      ? event.pricingOptions
      : [];
    const pricingLabelById = new Map(
      pricingOptions
        .map((option) => [
          String(option?.optionId || "").trim(),
          String(option?.label || "").trim() || "Teilnahmegebühr",
        ])
        .filter(([optionId]) => Boolean(optionId)),
    );
    const contractCountById = new Map();
    (Array.isArray(event?.participants) ? event.participants : []).forEach(
      (entry) => {
        const contractId = toId(entry?.contractId);
        if (!contractId) return;
        contractCountById.set(
          contractId,
          (contractCountById.get(contractId) || 0) + 1,
        );
      },
    );

    const byId = new Map();
    (Array.isArray(event?.participants) ? event.participants : []).forEach(
      (entry) => {
        const member = entry?.memberId;
        const id = toId(member);
        if (!id) return;
        const contractId = toId(entry?.contractId);
        const selectedPricingOptionId = String(
          entry?.selectedPricingOptionId || "",
        ).trim();
        const siblingCount = contractId
          ? contractCountById.get(contractId) || 0
          : 0;

        byId.set(id, {
          id,
          label: toMemberName(member),
          degree: member?.degree,
          memberNumber: member?.memberNumber || "",
          photoOptOut: Boolean(entry?.photoOptOut),
          contractId,
          selectedPricingOptionId: selectedPricingOptionId || undefined,
          pricingOptionLabel: selectedPricingOptionId
            ? pricingLabelById.get(selectedPricingOptionId) ||
              selectedPricingOptionId
            : "",
          paidAmount:
            typeof entry?.paidAmount === "number" &&
            Number.isFinite(entry.paidAmount)
              ? Number(entry.paidAmount)
              : undefined,
          siblingCount,
          hasSiblingDiscount: siblingCount > 1,
          createdAt: entry?.createdAt,
        });
      },
    );
    return Array.from(byId.values());
  }, [event?.participants, event?.pricingOptions]);

  const participantsWithConsent = useMemo(
    () => registeredParticipants.filter((entry) => !entry.photoOptOut),
    [registeredParticipants],
  );
  const participantsWithoutConsent = useMemo(
    () => registeredParticipants.filter((entry) => entry.photoOptOut),
    [registeredParticipants],
  );
  const siblingDiscountEnabled = Boolean(event?.siblingDiscount?.enabled);
  const activeConsentParticipants =
    consentTab === 0 ? participantsWithConsent : participantsWithoutConsent;

  // Members currently linked to this event (manual attendees + member registrations)
  const eventMembers = useMemo(() => {
    const map = new Map();
    const addMember = (member) => {
      if (!member) return;
      const id = toId(member);
      if (!id || id === "undefined" || id === "null") return;
      if (!map.has(id)) map.set(id, member);
    };

    (Array.isArray(event?.hasAttendees) ? event.hasAttendees : []).forEach(
      addMember,
    );
    (Array.isArray(event?.participants) ? event.participants : []).forEach(
      (entry) => addMember(entry?.memberId),
    );

    return Array.from(map.values());
  }, [event]);

  // Current attendee ids
  const attendeeIds = useMemo(
    () => new Set(eventMembers.map((member) => toId(member))),
    [eventMembers],
  );

  // Current attendees for display
  const currentAttendees = useMemo(() => {
    return eventMembers.map((a) => {
      const id = toId(a);
      const label = toMemberName(a);
      return {
        id,
        label,
        degree: a?.degree,
      };
    });
  }, [eventMembers]);
  const attendeeOnly = useMemo(() => {
    const registeredSet = new Set(
      registeredParticipants.map((entry) => String(entry.id)),
    );
    return currentAttendees.filter(
      (entry) => !registeredSet.has(String(entry.id)),
    );
  }, [currentAttendees, registeredParticipants]);

  // ---- Manual tab state ----
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortKey, setSortKey] = useState("lastName");
  const [sortOrder, setSortOrder] = useState("asc");

  // ---- Criteria tab state ----
  const [criteriaForm, setCriteriaForm] = useState({
    siteId: "",
    minKup: "",
    ageMin: "",
    ageMax: "",
    activeOnly: true,
  });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setSiteFilter("ALL");
      setSearch("");
      setManagementTab(0);
      setConsentTab(0);
      setPreview(null);
      setCriteriaForm({
        siteId: "",
        minKup: "",
        ageMin: "",
        ageMax: "",
        activeOnly: true,
      });
    }
  }, [open]);

  // Build site options from the sites query
  const siteOptions = useMemo(() => {
    return (sites || []).map((s) => ({
      id: s._id || s.id,
      siteName: s.siteName || s._id,
    }));
  }, [sites]);

  // Active members not yet in event
  const activeNotAttending = useMemo(
    () =>
      (members || [])
        .filter((m) => m?.status === "active")
        .filter((m) => !attendeeIds.has(toId(m))),
    [members, attendeeIds],
  );

  const filtered = useMemo(() => {
    let arr = activeNotAttending;

    if (siteFilter !== "ALL") {
      arr = arr.filter((m) => {
        const sr = m?.standortRef;
        const id = typeof sr === "object" ? sr?._id : sr;
        return id === siteFilter;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((m) =>
        [
          m?.firstName ?? "",
          m?.lastName ?? "",
          m?.memberNumber ?? "",
          (typeof m?.standortRef === "object" ? m.standortRef?.siteName : "") ??
            "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }

    const order = sortOrder === "asc" ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      const av = a?.[sortKey] ?? "";
      const bv = b?.[sortKey] ?? "";
      if (sortKey === "degree") {
        return ((Number(av) || 0) - (Number(bv) || 0)) * order;
      }
      return (
        String(av).localeCompare(String(bv), undefined, { numeric: true }) *
        order
      );
    });

    return arr;
  }, [activeNotAttending, siteFilter, search, sortKey, sortOrder]);

  const toggleAll = (checked) => {
    setSelectedIds(checked ? filtered.map((m) => toId(m)) : []);
  };

  const isSelected = (id) => selectedIds.includes(id);
  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAddSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await addAttendees({ id: eventId, memberIds: selectedIds }).unwrap();
      setSelectedIds([]);
      await refetch();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Teilnehmer hinzugefügt",
          data: {
            successText: `${selectedIds.length} Teilnehmer hinzugefügt.`,
          },
        }),
      );
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Hinzufügen fehlgeschlagen." },
        }),
      );
    }
  };

  const handleRemoveOne = async (id) => {
    try {
      await removeAttendees({ id: eventId, memberIds: [id] }).unwrap();
      await refetch();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Entfernen fehlgeschlagen." },
        }),
      );
    }
  };

  // ---- Criteria handlers ----
  const buildCriteria = () => {
    const c = {};
    if (criteriaForm.siteId) c.siteId = criteriaForm.siteId;
    if (criteriaForm.minKup !== "") c.minKup = Number(criteriaForm.minKup);
    if (criteriaForm.ageMin !== "") c.ageMin = Number(criteriaForm.ageMin);
    if (criteriaForm.ageMax !== "") c.ageMax = Number(criteriaForm.ageMax);
    if (criteriaForm.activeOnly) c.activeOnly = true;
    return c;
  };

  const handlePreview = async () => {
    try {
      const result = await previewByCriteria({
        id: eventId,
        criteria: buildCriteria(),
      }).unwrap();
      setPreview(result);
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Vorschau fehlgeschlagen." },
        }),
      );
    }
  };

  const handleAddByCriteria = async () => {
    try {
      const result = await addByCriteria({
        id: eventId,
        criteria: buildCriteria(),
      }).unwrap();
      setPreview(null);
      await refetch();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Teilnehmer hinzugefügt",
          data: {
            successText: `${result.added} Teilnehmer hinzugefügt (gesamt: ${result.total}).`,
          },
        }),
      );
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Hinzufügen fehlgeschlagen." },
        }),
      );
    }
  };

  const busy =
    isFetchingEvent ||
    isLoadingMembers ||
    isAdding ||
    isRemoving ||
    isAddingByCriteria;

  return (
    <Dialog
      open={open}
      onClose={() => onClose?.(false)}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>Teilnehmer · {eventTitle}</DialogTitle>

      <DialogContent dividers>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            background:
              "linear-gradient(180deg, rgba(14, 165, 233, 0.08) 0%, rgba(14, 165, 233, 0.01) 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Stack spacing={0.25}>
              <Typography variant="subtitle1">
                Anmeldungen & Einwilligung
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Öffentliche Event-Anmeldungen mit Bildfreigabe-Status.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                color="success"
                label={`Bildfreigabe: ${participantsWithConsent.length}`}
              />
              <Chip
                size="small"
                color="error"
                label={`Keine Freigabe: ${participantsWithoutConsent.length}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Gesamt: ${registeredParticipants.length}`}
              />
            </Stack>
          </Stack>

          <Tabs
            value={consentTab}
            onChange={(_, nextTab) => setConsentTab(nextTab)}
          >
            <Tab label={`Bildfreigabe (${participantsWithConsent.length})`} />
            <Tab
              label={`Keine Bildfreigabe (${participantsWithoutConsent.length})`}
            />
          </Tabs>

          <Box sx={{ mt: 1.5 }}>
            {activeConsentParticipants.length === 0 ? (
              <Alert severity={consentTab === 0 ? "info" : "warning"}>
                {consentTab === 0
                  ? "Keine Teilnehmer mit Bildfreigabe."
                  : "Keine Teilnehmer ohne Bildfreigabe."}
              </Alert>
            ) : (
              <Stack spacing={1}>
                {activeConsentParticipants.map((entry) => (
                  <Paper key={entry.id} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {entry.degree != null
                            ? `${entry.label} · ${gradLabel(entry.degree)}`
                            : entry.label}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {entry.memberNumber && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Nr. ${entry.memberNumber}`}
                            />
                          )}
                          <Chip
                            size="small"
                            color={entry.photoOptOut ? "error" : "success"}
                            label={
                              entry.photoOptOut
                                ? "Keine Bildfreigabe"
                                : "Bildfreigabe"
                            }
                          />
                          {entry.selectedPricingOptionId && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`Preisoption: ${entry.pricingOptionLabel}`}
                            />
                          )}
                          {typeof entry.paidAmount === "number" && (
                            <Chip
                              size="small"
                              color="secondary"
                              variant="outlined"
                              label={`Betrag: ${formatCurrency(
                                entry.paidAmount,
                              )}`}
                            />
                          )}
                          {siblingDiscountEnabled &&
                            entry.hasSiblingDiscount && (
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label={`Geschwister (${entry.siblingCount})`}
                              />
                            )}
                        </Stack>
                        {entry.createdAt && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            Angemeldet am{" "}
                            {dayjs(entry.createdAt).format("DD.MM.YYYY HH:mm")}
                          </Typography>
                        )}
                      </Stack>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<Icon icon="mdi:close" />}
                        onClick={() => handleRemoveOne(entry.id)}
                      >
                        Entfernen
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {attendeeOnly.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Weitere Teilnehmer aus Liste ({attendeeOnly.length})
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {attendeeOnly.map((a) => (
                  <Chip
                    key={a.id}
                    label={
                      a.degree != null
                        ? `${a.label} · ${gradLabel(a.degree)}`
                        : a.label
                    }
                    size="small"
                    onDelete={() => handleRemoveOne(a.id)}
                    deleteIcon={<Icon icon="mdi:close" />}
                  />
                ))}
              </Stack>
            </>
          )}
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.5, color: "text.secondary" }}
          >
            Um Teilnehmer zu entfernen, auf "Entfernen" oder das "X" klicken.
          </Typography>
        </Paper>

        <Tabs value={managementTab} onChange={(_, v) => setManagementTab(v)}>
          <Tab label="Manuell" />
          <Tab label="Nach Kriterien" />
        </Tabs>

        {/* ---- Manual Tab ---- */}
        <TabPanel value={managementTab} index={0}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Paper variant="outlined" sx={{ flex: 1, overflow: "hidden" }}>
              <Toolbar sx={{ gap: 2, mt: 1, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Standort</InputLabel>
                  <Select
                    label="Standort"
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">Alle Standorte</MenuItem>
                    {siteOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.siteName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Suche (Name, Nr.)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
              </Toolbar>

              <Divider />

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedIds.length > 0 &&
                            selectedIds.length < filtered.length
                          }
                          checked={
                            filtered.length > 0 &&
                            selectedIds.length === filtered.length
                          }
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortKey === "lastName"}
                          direction={sortKey === "lastName" ? sortOrder : "asc"}
                          onClick={() => {
                            if (sortKey === "lastName")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("lastName");
                          }}
                        >
                          Nachname
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortKey === "firstName"}
                          direction={
                            sortKey === "firstName" ? sortOrder : "asc"
                          }
                          onClick={() => {
                            if (sortKey === "firstName")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("firstName");
                          }}
                        >
                          Vorname
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortKey === "degree"}
                          direction={sortKey === "degree" ? sortOrder : "asc"}
                          onClick={() => {
                            if (sortKey === "degree")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("degree");
                          }}
                        >
                          Grad
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Nr.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((m) => {
                      const id = toId(m);
                      const checked = isSelected(id);
                      return (
                        <TableRow
                          key={id}
                          hover
                          selected={checked}
                          onClick={() => toggleOne(id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleOne(id)}
                            />
                          </TableCell>
                          <TableCell>{m?.lastName}</TableCell>
                          <TableCell>{m?.firstName}</TableCell>
                          <TableCell>{gradLabel(m?.degree)}</TableCell>
                          <TableCell>{m?.memberNumber ?? ""}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!busy && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography
                            variant="body2"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            Keine Mitglieder gefunden.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Stack sx={{ width: { xs: "100%", md: 300 } }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ausgewählt ({selectedIds.length})
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {filtered
                    .filter((m) => selectedIds.includes(toId(m)))
                    .map((m) => (
                      <Chip
                        key={toId(m)}
                        label={`${m.lastName}, ${m.firstName}`}
                        size="small"
                        onDelete={() => toggleOne(toId(m))}
                      />
                    ))}
                </Stack>
              </Paper>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Icon icon="mdi:account-multiple-plus" />}
                disabled={busy || selectedIds.length === 0}
                onClick={handleAddSelected}
              >
                {selectedIds.length} Teilnehmer hinzufügen
              </Button>
            </Stack>
          </Stack>
        </TabPanel>

        {/* ---- Criteria Tab ---- */}
        <TabPanel value={managementTab} index={1}>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Mitglieder nach Kriterien filtern und zur Veranstaltung
              hinzufügen.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Standort</InputLabel>
                <Select
                  label="Standort"
                  value={criteriaForm.siteId}
                  onChange={(e) =>
                    setCriteriaForm((p) => ({ ...p, siteId: e.target.value }))
                  }
                >
                  <MenuItem value="">Alle Standorte</MenuItem>
                  {siteOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.siteName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Ab Gürtelgrad</InputLabel>
                <Select
                  label="Ab Gürtelgrad"
                  value={criteriaForm.minKup}
                  onChange={(e) =>
                    setCriteriaForm((p) => ({ ...p, minKup: e.target.value }))
                  }
                >
                  <MenuItem value="">Alle</MenuItem>
                  {gradArray.map((g) => (
                    <MenuItem key={g.value} value={g.value}>
                      ab {g.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                label="Alter ab"
                type="number"
                value={criteriaForm.ageMin}
                onChange={(e) =>
                  setCriteriaForm((p) => ({ ...p, ageMin: e.target.value }))
                }
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Alter bis"
                type="number"
                value={criteriaForm.ageMax}
                onChange={(e) =>
                  setCriteriaForm((p) => ({ ...p, ageMax: e.target.value }))
                }
                sx={{ maxWidth: 140 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={criteriaForm.activeOnly}
                    onChange={(e) =>
                      setCriteriaForm((p) => ({
                        ...p,
                        activeOnly: e.target.checked,
                      }))
                    }
                  />
                }
                label="Nur aktive Mitglieder"
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={isPreviewing}
                startIcon={
                  isPreviewing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon icon="mdi:eye-outline" />
                  )
                }
              >
                Vorschau
              </Button>
            </Stack>

            {preview && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Vorschau-Ergebnis</Typography>
                  <Typography variant="body2">
                    Gesamt gefunden: <strong>{preview.totalMatching}</strong>
                  </Typography>
                  {preview.alreadyAdded > 0 && (
                    <Alert severity="info" sx={{ py: 0 }}>
                      {preview.alreadyAdded} Mitglieder sind bereits Teilnehmer
                      (werden übersprungen).
                    </Alert>
                  )}
                  <Typography variant="body2">
                    Werden hinzugefügt: <strong>{preview.willBeAdded}</strong>
                  </Typography>

                  {preview.members?.length > 0 && (
                    <TableContainer sx={{ maxHeight: 250 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nachname</TableCell>
                            <TableCell>Vorname</TableCell>
                            <TableCell>Grad</TableCell>
                            <TableCell>Standort</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preview.members.map((m) => (
                            <TableRow key={m._id}>
                              <TableCell>{m.lastName}</TableCell>
                              <TableCell>{m.firstName}</TableCell>
                              <TableCell>{gradLabel(m.degree)}</TableCell>
                              <TableCell>
                                {m.standortRef?.siteName ?? ""}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleAddByCriteria}
                    disabled={
                      isAddingByCriteria || (preview.willBeAdded || 0) === 0
                    }
                    startIcon={<Icon icon="mdi:account-multiple-plus" />}
                  >
                    {preview.willBeAdded} Teilnehmer hinzufügen
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose?.(false)}>Schließen</Button>
        <Button
          onClick={() => onClose?.(true)}
          variant="contained"
          disabled={busy}
        >
          Fertig
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EventParticipantsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  eventId: PropTypes.string.isRequired,
  eventTitle: PropTypes.string,
};
